import { Modal, Spinner } from "@heroui/react";
import { useEffect, useRef, useState } from "react";

import { TablePager } from "@/components/kit";
import {
  PersonAvatar,
  formatDayTime,
} from "@/components/sales/shared";
import { useGetZeroResultEvents } from "@/hooks/api/zero-result-search/useGetZeroResultEvents";
import type { ZeroResultQuery } from "@/types";

interface EventsModalProps {
  query: ZeroResultQuery | null;
  isOpen: boolean;
  onClose: () => void;
}

const EVENTS_PER_PAGE = 10;
const th = "text-muted px-3 py-2 text-start text-xs font-medium";

export function EventsModal({ query, isOpen, onClose }: EventsModalProps) {
  const [page, setPage] = useState(1);
  // Keep the last query while the dialog fades out.
  const last = useRef<ZeroResultQuery | null>(null);

  if (query) last.current = query;
  const shown = query ?? last.current;

  useEffect(() => {
    setPage(1);
  }, [query?.id]);

  const { data: events, isFetching } = useGetZeroResultEvents(
    query?.id ?? "",
    page,
    EVENTS_PER_PAGE,
    isOpen && !!query,
  );

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && onClose()}
    >
      <Modal.Container placement="center" scroll="inside">
        <Modal.Dialog className="w-full max-w-2xl">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>
              Search events for &ldquo;{shown?.searchTerm}&rdquo;
            </Modal.Heading>
          </Modal.Header>
          <Modal.Body className="flex flex-col p-1">
            {isFetching && !events ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-separator border-b">
                      <th className={th}>User</th>
                      <th className={th}>Phone</th>
                      <th className={th}>Searched at</th>
                    </tr>
                  </thead>
                  <tbody className="divide-separator divide-y">
                    {(events?.data ?? []).map((event) => {
                      const name = `${event.user.firstName} ${event.user.lastName}`;

                      return (
                        <tr key={event.id}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-3">
                              <PersonAvatar name={name} />
                              <span className="font-medium">{name}</span>
                            </div>
                          </td>
                          <td className="px-3 tabular-nums">
                            {event.user.phoneNumber}
                          </td>
                          <td className="text-muted px-3 tabular-nums">
                            {formatDayTime(event.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {events && events.data.length === 0 ? (
                  <p className="text-muted py-8 text-center text-sm">
                    No events found
                  </p>
                ) : null}
                {events ? (
                  <TablePager
                    page={page}
                    pageSize={EVENTS_PER_PAGE}
                    total={events.total ?? events.data.length}
                    onPageChange={setPage}
                  />
                ) : null}
              </>
            )}
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
